import { useState } from 'react';
import {
  Box,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Collapse,
  Typography,
} from '@mui/material';
import { FolderIcon, FolderOpenIcon, FileTextIcon, ChevronRightIcon } from 'lucide-react';
import type { DocTreeNode } from '@/lib/types';

interface DocsTreeProps {
  tree: DocTreeNode[];
  selectedPath: string | null;
  onSelect: (path: string) => void;
}

interface TreeNodeProps {
  node: DocTreeNode;
  selectedPath: string | null;
  onSelect: (path: string) => void;
  level?: number;
}

function TreeNode({ node, selectedPath, onSelect, level = 0 }: TreeNodeProps) {
  const [open, setOpen] = useState(false);

  const handleClick = () => {
    if (node.type === 'file') {
      onSelect(node.path);
    } else {
      setOpen(!open);
    }
  };

  const isSelected = node.type === 'file' && selectedPath === node.path;

  return (
    <>
      <ListItemButton
        onClick={handleClick}
        selected={isSelected}
        sx={{
          pl: 2 + level * 2,
          py: 0.5,
          '&:hover': {
            backgroundColor: 'action.hover',
          },
        }}
      >
        <ListItemIcon sx={{ minWidth: 32 }}>
          {node.type === 'dir' ? (
            open ? (
              <FolderOpenIcon size={18} />
            ) : (
              <FolderIcon size={18} />
            )
          ) : (
            <FileTextIcon size={18} />
          )}
        </ListItemIcon>
        <ListItemText
          primary={
            <Typography variant="body2" sx={{ fontSize: '0.875rem' }}>
              {node.name}
            </Typography>
          }
        />
        {node.type === 'dir' && (
          <ChevronRightIcon
            size={16}
            style={{
              transform: open ? 'rotate(90deg)' : 'rotate(0deg)',
              transition: 'transform 0.2s',
            }}
          />
        )}
      </ListItemButton>
      {node.type === 'dir' && (
        <Collapse in={open} timeout="auto" unmountOnExit>
          <List component="div" disablePadding>
            {node.children.map((child) => (
              <TreeNode
                key={child.path}
                node={child}
                selectedPath={selectedPath}
                onSelect={onSelect}
                level={level + 1}
              />
            ))}
          </List>
        </Collapse>
      )}
    </>
  );
}

export function DocsTree({ tree, selectedPath, onSelect }: DocsTreeProps) {
  return (
    <Box
      sx={{
        width: '100%',
        height: '100%',
        overflow: 'auto',
        bgcolor: 'background.paper',
      }}
    >
      <List component="nav" dense>
        {tree.map((node) => (
          <TreeNode key={node.path} node={node} selectedPath={selectedPath} onSelect={onSelect} />
        ))}
      </List>
    </Box>
  );
}
